import React, { useState, useMemo } from "react";
import { Database, FileText } from "lucide-react";
import { FileExcelTwoTone, FileTextTwoTone } from "@ant-design/icons";
import CatalogRowPreview from '../CatalogRowPreview';
import useCatalogAssetAvailability from '../../hooks/useCatalogAssetAvailability';
import styles from './DocumentProcessor.module.css';
import * as XLSX from "xlsx";

const FIELD_ALIASES = {
    part: ["name", "part number", "part_number", "partnumber", "артикул", "номенклатура", "наименование", "наименование компонента"],
    manufacturer: ["wrk", "manufacturer", "производитель", "brand"],
    qty: ["qty", "quantity", "количество", "кол-во", "запрошенное количество"],
    price: ["price", "цена", "прайс"]
};

const normalizeKey = (value) => String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const findFieldHeader = (headers, field) => {
    const aliases = FIELD_ALIASES[field] || [];
    const normalizedAliases = aliases.map(normalizeKey);

    return headers.find(header => normalizedAliases.includes(normalizeKey(header))) || null;
};

const isZeroValue = (value) => {
    const text = String(value ?? "").trim();
    if (!text) return false;

    const numeric = Number(text.replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(numeric) && numeric === 0;
};

const ProcessingAnimation = () => (
    <div className={styles.processingAnimation} role="status" aria-live="polite">
        <div className={styles.processingVisual} aria-hidden="true">
            <div className={styles.processingGlow} />
            <div className={styles.documentSheet}>
                <div className={styles.documentFold} />
                <div className={styles.sheetHeading} />
                <div className={styles.sheetRows}>
                    {Array.from({ length: 5 }, (_, index) => (
                        <div key={index}><span /><span /><span /></div>
                    ))}
                </div>
                <div className={styles.scanLine} />
            </div>
            <div className={styles.floatingData}>
                <span>01</span>
                <span>ABC</span>
                <span>24.8</span>
            </div>
            <div className={styles.resultBadge}>
                <span>✓</span>
                <div><i /><i /><i /></div>
            </div>
        </div>

        <strong>Разбираем данные документа</strong>
        <span className={styles.processingCaption}>Распознаём строки и собираем структуру таблицы…</span>
        <div className={styles.processingDots} aria-hidden="true"><i /><i /><i /></div>
    </div>
);

export default function ResultPanel({
    loading,
    processedText,
    processedTable,
    fileName,
    setFileName,
    onSave,
    saveStatus,
    sourceWorkbookBase64 = "",
    onTableChange,
    documentType = "request",
    catalogAppearance = false,
}) {
    const [editingRowIndex, setEditingRowIndex] = useState(null);
    const [editingRow, setEditingRow] = useState({});
    const [zeroWarningOpen, setZeroWarningOpen] = useState(false);
    const [zeroWarningEdits, setZeroWarningEdits] = useState({});

    const hasTable =
        processedTable?.headers?.length > 0 &&
        Array.isArray(processedTable?.data);

    const hasErrors = useMemo(
        () => hasTable && processedTable.data.some(r => r._isErrorRow),
        [processedTable, hasTable]
    );
    const visibleHeaders = useMemo(() => {
        if (!hasTable) return [];

        return processedTable.headers.filter(header => {
            const normalized = String(header || "").trim().toLowerCase();
            return !(documentType === "request" && normalized === "part number");
        });
    }, [documentType, hasTable, processedTable]);

    const partHeader = findFieldHeader(visibleHeaders, "part");
    const manufacturerHeader = hasTable ? findFieldHeader(processedTable.headers, "manufacturer") : null;
    const catalogRows = useMemo(() => !hasTable || !partHeader ? [] : processedTable.data
        .filter(row => !row._isErrorRow)
        .map(row => ({ name: String(row[partHeader] || '').trim() })), [hasTable, partHeader, processedTable]);
    const catalogAssets = useCatalogAssetAvailability(catalogRows, catalogAppearance && !loading && hasTable, true);

    const zeroCheckHeaders = useMemo(() => {
        if (!hasTable) return { qty: null, price: null };

        return {
            qty: findFieldHeader(processedTable.headers, "qty"),
            price: findFieldHeader(processedTable.headers, "price")
        };
    }, [hasTable, processedTable]);

    const zeroWarningRows = useMemo(() => {
        if (!hasTable) return [];

        const getIssueFields = (row) => {
            const fields = [];

            if (zeroCheckHeaders.qty && isZeroValue(row[zeroCheckHeaders.qty])) {
                fields.push(zeroCheckHeaders.qty);
            }

            if (documentType === "offer" && zeroCheckHeaders.price && isZeroValue(row[zeroCheckHeaders.price])) {
                fields.push(zeroCheckHeaders.price);
            }

            return fields;
        };

        return processedTable.data
            .map((row, index) => ({
                index,
                row,
                fields: getIssueFields(row)
            }))
            .filter(item => item.fields.length > 0);
    }, [documentType, hasTable, processedTable, zeroCheckHeaders]);

    const zeroWarningRowIndexes = useMemo(
        () => new Set(zeroWarningRows.map(item => item.index)),
        [zeroWarningRows]
    );

    const formatHeader = (header) => {
        const normalized = String(header || "").trim().toLowerCase();
        return normalized === "row_number" || normalized === "row-number" ? "row" : header;
    };

    const downloadCSV = () => {
        if (!processedText) return;
        const blob = new Blob([processedText], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "processed.csv";
        a.click();
    };

    const getCleanRows = (table = processedTable) => {
        if (!table?.headers?.length || !Array.isArray(table?.data)) return [];

        return table.data.map(r => {
            const copy = { ...r };
            delete copy._isErrorRow;
            return copy;
        });
    };

    const appendCleanedDataSheet = (wb, table = processedTable) => {
        const ws = XLSX.utils.json_to_sheet(getCleanRows(table), {
            header: table.headers
        });
        XLSX.utils.book_append_sheet(wb, ws, "cleaned data");
    };

    const getSourceWorkbookRows = () => {
        if (!sourceWorkbookBase64) return null;

        const sourceWb = XLSX.read(sourceWorkbookBase64, { type: "base64" });
        const sourceSheet = sourceWb.Sheets[sourceWb.SheetNames[0]];
        const sourceRows = XLSX.utils.sheet_to_json(sourceSheet, {
            header: 1,
            raw: false,
            defval: ""
        });

        if (!sourceRows.length) return null;

        const dataRows = sourceRows.map(row => row.slice(2));
        const originalRows = sourceRows.map((row, index) => [
            index === 0 ? "clean part number" : (row[1] || ""),
            ...row.slice(2)
        ]);

        return { dataRows, originalRows };
    };

    const buildExcelWorkbook = (table = processedTable) => {
        const wb = XLSX.utils.book_new();
        const sourceRows = getSourceWorkbookRows();

        if (sourceRows) {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sourceRows.dataRows), "data");
        }

        appendCleanedDataSheet(wb, table);

        if (sourceRows) {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sourceRows.originalRows), "original");
        }

        return wb;
    };

    const workbookToByteArray = (wb) => {
        const arrayBuffer = XLSX.write(wb, {
            bookType: "xlsx",
            type: "array"
        });

        return Array.from(new Uint8Array(arrayBuffer));
    };

    const base64ToByteArray = (base64) => {
        const binary = window.atob(base64);
        const bytes = new Uint8Array(binary.length);

        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }

        return Array.from(bytes);
    };

    const downloadBase64Workbook = (base64) => {
        const bytes = base64ToByteArray(base64);
        const blob = new Blob([new Uint8Array(bytes)], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");

        a.href = url;
        a.download = "processed.xlsx";
        a.click();
        URL.revokeObjectURL(url);
    };

    const downloadExcel = () => {
        if (!hasTable) return;

        if (sourceWorkbookBase64) {
            downloadBase64Workbook(sourceWorkbookBase64);
            return;
        }

        XLSX.writeFile(buildExcelWorkbook(), "processed.xlsx");
    };

    const buildBinaryFile = (table = processedTable) => {
        const shouldUseSourceWorkbook = sourceWorkbookBase64 && table === processedTable;
        const binaryFile = shouldUseSourceWorkbook
            ? base64ToByteArray(sourceWorkbookBase64)
            : table?.headers?.length
                ? workbookToByteArray(buildExcelWorkbook(table))
                : null;

        return binaryFile;
    };

    const commitSave = (table = processedTable) => {
        onSave(buildBinaryFile(table), table);
    };

    const openZeroWarning = () => {
        const edits = {};
        zeroWarningRows.forEach(({ index, row }) => {
            edits[index] = { ...row };
        });
        setZeroWarningEdits(edits);
        setZeroWarningOpen(true);
    };

    const handleSave = () => {
        if (zeroWarningRows.length > 0) {
            openZeroWarning();
            return;
        }

        commitSave();
    };

    const startRowEdit = (rowIndex) => {
        setEditingRowIndex(rowIndex);
        setEditingRow({ ...(processedTable.data[rowIndex] || {}) });
    };

    const cancelRowEdit = () => {
        setEditingRowIndex(null);
        setEditingRow({});
    };

    const updateEditingCell = (header, value) => {
        setEditingRow(prev => ({
            ...prev,
            [header]: value
        }));
    };

    const saveRowEdit = () => {
        if (editingRowIndex === null || !hasTable) {
            return;
        }

        const nextData = processedTable.data.map((row, index) => (
            index === editingRowIndex
                ? {
                    ...editingRow,
                    _isErrorRow: row._isErrorRow
                }
                : row
        ));

        onTableChange?.({
            ...processedTable,
            data: nextData
        });
        cancelRowEdit();
    };

    const updateZeroWarningCell = (rowIndex, header, value) => {
        setZeroWarningEdits(prev => ({
            ...prev,
            [rowIndex]: {
                ...(prev[rowIndex] || {}),
                [header]: value
            }
        }));
    };

    const buildTableWithZeroWarningEdits = () => ({
        ...processedTable,
        data: processedTable.data.map((row, index) => (
            zeroWarningEdits[index]
                ? {
                    ...zeroWarningEdits[index],
                    _isErrorRow: row._isErrorRow
                }
                : row
        ))
    });

    const applyZeroWarningEdits = () => {
        const nextTable = buildTableWithZeroWarningEdits();
        onTableChange?.(nextTable);
        return nextTable;
    };

    const saveWithZeroWarningEdits = () => {
        const nextTable = applyZeroWarningEdits();
        setZeroWarningOpen(false);
        commitSave(nextTable);
    };

    const saveWithoutZeroWarningEdits = () => {
        setZeroWarningOpen(false);
        commitSave();
    };

    return (
        <div className={styles.resultPanelContent}>
            <h3>Результат обработки:</h3>

            <div className={styles.resultToolbar}>
            {/* === Экспорт === */}
            <div className={styles.inlineButtons}>
                {hasTable && !hasErrors && (
                    <>
                        <button type="button" className={`${styles.btn} ${styles.btnGreen} ${styles.exportIconButton}`} onClick={downloadCSV} title="Скачать CSV" aria-label="Скачать CSV">
                            <FileTextTwoTone twoToneColor="var(--primary)" style={{fontSize:28}} aria-hidden="true" /><span className={styles.exportFormat}>CSV</span>
                        </button>
                        <button type="button" className={`${styles.btn} ${styles.btnGreen} ${styles.exportIconButton}`} onClick={downloadExcel} title="Скачать Excel" aria-label="Скачать Excel">
                            <FileExcelTwoTone twoToneColor="var(--success)" style={{fontSize:28}} aria-hidden="true" />
                        </button>
                    </>
                )}
            </div>

            {/* === SAVE === */}
            <div className={styles.saveControls}>
                <input
                    className={styles.fileNameInput}
                    placeholder="Введите имя файла"
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    disabled={!hasTable || hasErrors}
                />

                <button
                    className={`${styles.btn} ${styles.btnGreen}`}
                    onClick={handleSave}
                    disabled={!fileName || hasErrors || saveStatus?.loading}
                >
                    {catalogAppearance && <Database size={23} aria-hidden="true" />}
                    {saveStatus?.loading ? "Сохранение…" : "Сохранить"}
                </button>
            </div>

            </div>

            {catalogAssets.error && <small role="status">Не удалось проверить наличие карточек в каталоге. <button type="button" onClick={catalogAssets.retry}>Повторить проверку</button></small>}
            {saveStatus?.message && (
                <div className={saveStatus.error ? styles.error : styles.success}>
                    {saveStatus.message}
                </div>
            )}

            {loading ? (
                <ProcessingAnimation />
            ) : catalogAppearance && !hasTable && !processedText ? (
                <div className={styles.catalogEmpty}>
                    <FileText size={44} aria-hidden="true" />
                    <strong>Нет данных</strong>
                    <p>Загрузите BOM или введите текст запроса<br />для обработки</p>
                </div>
            ) : hasTable ? (
                    <div className={styles.tableWrapper}>
                        <table className={styles.resultTable}>
                            <thead>
                                <tr>
                                    <th className={styles.rowActionsHeader}>Действия</th>
                                    {visibleHeaders.map((h, i) => (
                                        <th key={i}>{formatHeader(h)}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {processedTable.data.map((row, r) => (
                                    <tr
                                        key={r}
                                        className={[
                                            row._isErrorRow ? styles.errorRow : "",
                                            !row._isErrorRow && zeroWarningRowIndexes.has(r) ? styles.zeroWarningRow : ""
                                        ].filter(Boolean).join(" ")}
                                    >
                                        <td className={styles.rowActionsCell}>
                                            {editingRowIndex === r ? (
                                                <div className={styles.rowActionButtons}>
                                                    <button
                                                        type="button"
                                                        className={styles.iconButton}
                                                        onClick={saveRowEdit}
                                                        title="Сохранить строку"
                                                        aria-label="Сохранить строку"
                                                    >
                                                        ✓
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={styles.iconButton}
                                                        onClick={cancelRowEdit}
                                                        title="Отменить"
                                                        aria-label="Отменить"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className={styles.iconButton}
                                                    onClick={() => startRowEdit(r)}
                                                    title="Редактировать строку"
                                                    aria-label="Редактировать строку"
                                                    disabled={loading}
                                                >
                                                    ✎
                                                </button>
                                            )}
                                        </td>
                                        {visibleHeaders.map((h, c) => (
                                            <td key={c}>
                                                {editingRowIndex === r ? (
                                                    <input
                                                        className={styles.tableCellInput}
                                                        value={editingRow[h] ?? ""}
                                                        onChange={(event) => updateEditingCell(h, event.target.value)}
                                                    />
                                                ) : (
                                                    <>{row[h]}{catalogAppearance && h === partHeader && !row._isErrorRow &&
                                                        <CatalogRowPreview partNumber={String(row[h] || '').trim()}
                                                            manufacturer={manufacturerHeader ? String(row[manufacturerHeader] || '') : ''}
                                                            materials={catalogAssets.materials[String(row[h] || '').trim()]} />}</>
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
            ) : (
                <div className={styles.placeholder}>Нет данных</div>
            )}

            {zeroWarningOpen && (
                <div className={styles.processingErrorBackdrop}>
                    <div className={styles.zeroWarningModal}>
                        <h3>Проверьте нулевые значения</h3>
                        <p>
                            Найдены строки с нулевым количеством{documentType === "offer" ? " или нулевой ценой" : ""}.
                            Можно исправить данные здесь или сохранить документ как есть.
                        </p>

                        <div className={styles.zeroWarningTableWrapper}>
                            <table className={styles.resultTable}>
                                <thead>
                                    <tr>
                                        <th>Строка</th>
                                        {visibleHeaders.map(header => (
                                            <th key={header}>{formatHeader(header)}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {zeroWarningRows.map(({ index, fields }) => (
                                        <tr key={index} className={styles.zeroWarningRow}>
                                            <td>{index + 1}</td>
                                            {visibleHeaders.map(header => (
                                                <td key={header}>
                                                    <input
                                                        className={`${styles.tableCellInput} ${fields.includes(header) ? styles.zeroWarningInput : ""}`}
                                                        value={zeroWarningEdits[index]?.[header] ?? ""}
                                                        onChange={(event) => updateZeroWarningCell(index, header, event.target.value)}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className={styles.processingErrorActions}>
                            <button type="button" className={`${styles.btn} ${styles.btnGreen}`} onClick={saveWithZeroWarningEdits}>
                                Применить и сохранить
                            </button>
                            <button type="button" className={styles.btn} onClick={saveWithoutZeroWarningEdits}>
                                Сохранить без изменений
                            </button>
                            <button type="button" className={styles.btn} onClick={() => setZeroWarningOpen(false)}>
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
